// Browse grid filters and tile facts, worked out from the synced products only
// (styleIndex styles: { name, brandName, code, colours, sizes, variants }).
import { compareSizes } from "./styleIndex";

// A style is "new in" when one of its variants was created in the last NEW_IN_DAYS.
export const NEW_IN_DAYS = 30;
// Price-under chips, in rupees. Only those that split the catalogue are shown.
export const PRICE_STEPS = [499, 999, 1499, 1999, 2999, 4999];
// A size with this many pieces or fewer reads as "low" on a tile's size strip.
export const LOW_STOCK = 2;

const DAY_MS = 24 * 60 * 60 * 1000;

const stockOf = (product) => {
  const n = Number(product?.onhandQty);
  return Number.isFinite(n) ? n : null;
};

const createdAt = (product) => {
  const t = Date.parse(product?.created || "");
  return Number.isFinite(t) ? t : null;
};

export function isNewIn(style, now = Date.now(), days = NEW_IN_DAYS) {
  return (style?.variants || []).some((v) => {
    const t = createdAt(v.product);
    return t !== null && t <= now && now - t <= days * DAY_MS;
  });
}

// Every size in the matching styles, in shop order (XS, S, M … then numbers).
export function sizeOptions(styles) {
  const all = new Set();
  for (const s of styles || []) for (const size of s.sizes || []) all.add(size);
  return [...all].sort(compareSizes);
}

export function colourOptions(styles) {
  const count = new Map();
  for (const s of styles || []) for (const c of s.colours || []) count.set(c, (count.get(c) || 0) + 1);
  return [...count.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([c]) => c);
}

// Steps that leave some styles out and keep some in; at most four.
export function priceOptions(styles) {
  const mins = (styles || []).map((s) => Number(s.minPrice)).filter(Number.isFinite);
  if (mins.length < 2) return [];
  return PRICE_STEPS.filter((step) => {
    const under = mins.filter((p) => p <= step).length;
    return under > 0 && under < mins.length;
  }).slice(0, 4);
}

const lc = (s) => String(s || "").toLowerCase();

// A typed query matches when every word is in the name, brand, style code or one
// of the style's colours ("navy linen" finds the navy linen shirt), or the whole
// query is a variant's item code or barcode.
export function matchesQuery(style, query) {
  const q = lc(query).trim();
  if (!q) return true;
  if ((style.variants || []).some((v) => lc(v.product?.value) === q || lc(v.product?.upc) === q)) return true;
  const hay = [style.name, style.brandName, style.code, ...(style.colours || [])].map(lc);
  return q.split(/\s+/).every((word) => hay.some((h) => h.includes(word)));
}

// The colour a query names, if any, so the tile and size sheet open on it.
export function queriedColour(style, query) {
  const words = lc(query).split(/\s+/).filter(Boolean);
  return (style.colours || []).find((c) => words.some((w) => lc(c) === w || (w.length >= 3 && lc(c).includes(w)))) || null;
}

// filters: { newIn, onOffer, size, colour, priceUnder }; onOffer is a test
// (style → truthy when it has a live markdown), since prices live on the till.
export function filterStyles(styles, filters = {}, now = Date.now()) {
  const { newIn, onOffer, size, colour, priceUnder } = filters;
  return (styles || []).filter((s) => {
    if (newIn && !isNewIn(s, now)) return false;
    if (onOffer && !onOffer(s)) return false;
    if (colour && !(s.colours || []).includes(colour)) return false;
    if (size && !(s.variants || []).some((v) => v.size === size && (!colour || v.colour === colour) && stockOf(v.product) !== 0)) return false;
    if (priceUnder && !(Number(s.minPrice) <= priceUnder)) return false;
    return true;
  });
}

// Per-size stock for a tile: [{ size, qty, state: "ok" | "low" | "out" | "unknown" }].
// Pieces are summed over the style's colours (or one colour when given).
export function sizeRun(style, colour = null) {
  const sizes = style?.sizes || [];
  if (sizes.length === 0) return [];
  return sizes.map((size) => {
    const variants = (style.variants || []).filter((v) => v.size === size && (!colour || v.colour === colour));
    const known = variants.map((v) => stockOf(v.product)).filter((n) => n !== null);
    if (known.length === 0) return { size, qty: null, state: "unknown" };
    const qty = known.reduce((n, q) => n + Math.max(q, 0), 0);
    return { size, qty, state: qty <= 0 ? "out" : qty <= LOW_STOCK ? "low" : "ok" };
  });
}

// Pieces in this store across the styles shown, or null when stock isn't synced.
export function piecesInStock(styles) {
  let total = 0;
  let known = false;
  for (const s of styles || []) {
    for (const v of s.variants || []) {
      const n = stockOf(v.product);
      if (n === null) continue;
      known = true;
      total += Math.max(n, 0);
    }
  }
  return known ? total : null;
}

// MRP above the selling price on the style's cheapest variant: { mrp, offPct } or null.
export function mrpFor(style) {
  let best = null;
  for (const v of style?.variants || []) {
    const price = Number(v.product?.sunitprice);
    const mrp = Number(v.product?.slistprice);
    if (!(price > 0) || !(mrp > price)) continue;
    if (!best || price < best.price) best = { price, mrp };
  }
  if (!best) return null;
  return { mrp: best.mrp, offPct: Math.round(((best.mrp - best.price) / best.mrp) * 100) };
}

// "Navy · 4 colours", "Navy", or "".
export function colourLine(style) {
  const n = style?.colours?.length || 0;
  if (n === 0) return "";
  return n === 1 ? style.colours[0] : `${style.colours[0]} · ${n} colours`;
}

// "synced just now" / "synced 12 min ago" / "synced 3 h ago" / "synced 2 days ago".
export function syncedLabel(at, now = Date.now()) {
  const t = typeof at === "number" ? at : Date.parse(at || "");
  if (!Number.isFinite(t)) return "";
  const min = Math.max(0, Math.round((now - t) / 60000));
  if (min < 1) return "synced just now";
  if (min < 60) return `synced ${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `synced ${h} h ago`;
  return `synced ${Math.round(h / 24)} days ago`;
}
